import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import LightBackground from '@/components/LightBackground';
import { 
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  ChevronDown,
  ChevronRight,
  GraduationCap,
  BookOpen,
  Loader2,
  RefreshCw,
  Check
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { academiesData } from '@/data/academyData';

interface Deliverable {
  id: string;
  module_id: string;
  title: string;
  sort_order: number;
}

interface Module {
  id: string;
  academy_id: string;
  module_number: number;
  title: string;
  description: string | null;
  full_description: string | null;
  transformation: string | null;
  skills: string[];
  tech: string[];
  is_active: boolean;
  deliverables?: Deliverable[];
}

interface Academy {
  id: string;
  name: string;
  tagline: string | null;
  description: string;
  sort_order: number;
  is_active: boolean;
  modules?: Module[];
}

const AdminContent = () => {
  const navigate = useNavigate();
  const { user, role, loading: authLoading } = useAuth();
  const [academies, setAcademies] = useState<Academy[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedAcademy, setExpandedAcademy] = useState<string | null>(null);
  const [expandedModule, setExpandedModule] = useState<string | null>(null);
  const [editingAcademy, setEditingAcademy] = useState<Academy | null>(null);
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [isCreatingAcademy, setIsCreatingAcademy] = useState(false);
  const [isCreatingModule, setIsCreatingModule] = useState<string | null>(null);
  const [newAcademy, setNewAcademy] = useState({ id: '', name: '', tagline: '', description: '' });
  const [newModule, setNewModule] = useState({ title: '', description: '', transformation: '', skills: '', tech: '' });

  // Removed custom cursor effect for light theme

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (role !== 'admin') {
        toast.error('Access denied. Admin privileges required.');
        navigate('/dashboard');
      }
    }
  }, [user, role, authLoading, navigate]);

  const loadContent = async () => {
    setLoading(true);
    try {
      // Fetch academies
      const { data: academiesData, error: academiesError } = await supabase
        .from('academies')
        .select('*')
        .order('sort_order', { ascending: true });

      if (academiesError) throw academiesError;

      // Fetch modules
      const { data: modulesData, error: modulesError } = await supabase
        .from('modules')
        .select('*')
        .order('module_number', { ascending: true });

      if (modulesError) throw modulesError;

      // Fetch deliverables
      const { data: deliverablesData, error: deliverablesError } = await supabase
        .from('module_deliverables')
        .select('*')
        .order('sort_order', { ascending: true });

      if (deliverablesError) throw deliverablesError;

      // Combine data
      const academiesWithModules = (academiesData || []).map(academy => ({
        ...academy,
        modules: (modulesData || [])
          .filter(m => m.academy_id === academy.id)
          .map(module => ({
            ...module,
            deliverables: (deliverablesData || []).filter(d => d.module_id === module.id)
          }))
      }));

      setAcademies(academiesWithModules);
    } catch (error) {
      console.error('Error loading content:', error);
      toast.error('Failed to load content');
    }
    setLoading(false);
  };

  const seedFromStaticData = async () => {
    try {
      toast.loading('Seeding academies from static data...');
      
      for (let i = 0; i < academiesData.length; i++) {
        const academy = academiesData[i];
        
        // Insert academy
        const { error: academyError } = await supabase
          .from('academies')
          .upsert({
            id: academy.id,
            name: academy.name,
            tagline: academy.tagline || null,
            description: academy.description,
            sort_order: i,
            is_active: true
          }, { onConflict: 'id' });

        if (academyError) throw academyError;

        // Insert modules
        for (const module of academy.modules) {
          const { data: moduleData, error: moduleError } = await supabase
            .from('modules')
            .upsert({
              academy_id: academy.id,
              module_number: module.id,
              title: module.title,
              description: module.description || null,
              full_description: module.fullDescription || null,
              transformation: module.transformation || null,
              skills: module.skills || [],
              tech: module.tech || [],
              is_active: true
            }, { onConflict: 'academy_id,module_number' })
            .select()
            .single();

          if (moduleError) throw moduleError;

          // Insert deliverables
          if (module.deliverables && moduleData) {
            for (let j = 0; j < module.deliverables.length; j++) {
              const { error: deliverableError } = await supabase
                .from('module_deliverables')
                .insert({
                  module_id: moduleData.id,
                  title: module.deliverables[j].title,
                  sort_order: j
                });

              if (deliverableError && !deliverableError.message.includes('duplicate')) {
                console.error('Deliverable error:', deliverableError);
              }
            }
          }
        }
      }

      toast.dismiss();
      toast.success('Content seeded successfully!');
      await loadContent();
    } catch (error) {
      toast.dismiss();
      console.error('Error seeding content:', error);
      toast.error('Failed to seed content');
    }
  };

  useEffect(() => {
    if (user && role === 'admin') {
      loadContent();
    }
  }, [user, role]);

  // Academy CRUD
  const handleCreateAcademy = async () => {
    if (!newAcademy.id || !newAcademy.name || !newAcademy.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const { error } = await supabase
        .from('academies')
        .insert({
          id: newAcademy.id.toLowerCase().replace(/\s+/g, '-'),
          name: newAcademy.name,
          tagline: newAcademy.tagline || null,
          description: newAcademy.description,
          sort_order: academies.length
        });

      if (error) throw error;
      toast.success('Academy created!');
      setIsCreatingAcademy(false);
      setNewAcademy({ id: '', name: '', tagline: '', description: '' });
      await loadContent();
    } catch (error) {
      console.error('Error creating academy:', error);
      toast.error('Failed to create academy');
    }
  };

  const handleUpdateAcademy = async () => {
    if (!editingAcademy) return;

    try {
      const { error } = await supabase
        .from('academies')
        .update({
          name: editingAcademy.name,
          tagline: editingAcademy.tagline,
          description: editingAcademy.description,
          is_active: editingAcademy.is_active
        })
        .eq('id', editingAcademy.id);

      if (error) throw error;
      toast.success('Academy updated!');
      setEditingAcademy(null);
      await loadContent();
    } catch (error) {
      console.error('Error updating academy:', error);
      toast.error('Failed to update academy');
    }
  };

  const handleDeleteAcademy = async (id: string) => {
    if (!confirm('Are you sure? This will delete all modules and deliverables in this academy.')) return;

    try {
      const { error } = await supabase
        .from('academies')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Academy deleted!');
      await loadContent();
    } catch (error) {
      console.error('Error deleting academy:', error);
      toast.error('Failed to delete academy');
    }
  };

  // Module CRUD
  const handleCreateModule = async (academyId: string) => {
    if (!newModule.title) {
      toast.error('Module title is required');
      return;
    }

    const academy = academies.find(a => a.id === academyId);
    const nextModuleNumber = (academy?.modules?.length || 0) + 1;

    try {
      const { error } = await supabase
        .from('modules')
        .insert({
          academy_id: academyId,
          module_number: nextModuleNumber,
          title: newModule.title,
          description: newModule.description || null,
          transformation: newModule.transformation || null,
          skills: newModule.skills ? newModule.skills.split(',').map(s => s.trim()) : [],
          tech: newModule.tech ? newModule.tech.split(',').map(t => t.trim()) : []
        });

      if (error) throw error;
      toast.success('Module created!');
      setIsCreatingModule(null);
      setNewModule({ title: '', description: '', transformation: '', skills: '', tech: '' });
      await loadContent();
    } catch (error) {
      console.error('Error creating module:', error);
      toast.error('Failed to create module');
    }
  };

  const handleUpdateModule = async () => {
    if (!editingModule) return;

    try {
      const { error } = await supabase
        .from('modules')
        .update({
          title: editingModule.title,
          description: editingModule.description,
          full_description: editingModule.full_description,
          transformation: editingModule.transformation,
          skills: editingModule.skills,
          tech: editingModule.tech,
          is_active: editingModule.is_active
        })
        .eq('id', editingModule.id);

      if (error) throw error;
      toast.success('Module updated!');
      setEditingModule(null);
      await loadContent();
    } catch (error) {
      console.error('Error updating module:', error);
      toast.error('Failed to update module');
    }
  };

  const handleDeleteModule = async (moduleId: string) => {
    if (!confirm('Are you sure? This will delete all deliverables in this module.')) return;

    try {
      const { error } = await supabase
        .from('modules')
        .delete()
        .eq('id', moduleId);

      if (error) throw error;
      toast.success('Module deleted!');
      await loadContent();
    } catch (error) {
      console.error('Error deleting module:', error);
      toast.error('Failed to delete module');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      <LightBackground variant="subtle" />

      {/* Header */}
      <motion.header
        className="relative z-20 p-6 flex justify-between items-center border-b border-border bg-white/80 backdrop-blur-sm"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center gap-4">
          <Link to="/admin">
            <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <span className="text-lg font-semibold text-foreground">
            Faceless Aura <span className="text-primary">Club</span>
          </span>
          <div className="h-6 w-px bg-border" />
          <span className="text-sm font-medium text-primary">Content Management</span>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-muted-foreground hover:text-foreground"
            onClick={loadContent}
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
          {academies.length === 0 && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={seedFromStaticData}
              className="text-primary border-primary/30"
            >
              Seed from Static Data
            </Button>
          )}
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 py-8">
        <motion.div
          className="mb-8 flex items-center justify-between"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div>
            <h1 className="text-2xl md:text-3xl font-light tracking-wide mb-2">
              <span className="text-foreground">Manage Academies & Modules</span>
            </h1>
            <p className="text-muted-foreground text-sm">
              {academies.length} academies • {academies.reduce((acc, a) => acc + (a.modules?.length || 0), 0)} modules
            </p>
          </div>
          <Button
            variant="default"
            onClick={() => setIsCreatingAcademy(true)}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Academy
          </Button>
        </motion.div>

        {/* Create Academy Form */}
        {isCreatingAcademy && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-6 border border-primary/30 bg-card/50 rounded-lg"
          >
            <h3 className="text-lg font-light text-foreground mb-4">Create New Academy</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider">ID (slug)</label>
                <Input
                  value={newAcademy.id}
                  onChange={(e) => setNewAcademy({ ...newAcademy, id: e.target.value })}
                  placeholder="e.g., advanced-marketing"
                  className="bg-background/50 border-border/30 mt-1"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider">Name</label>
                <Input
                  value={newAcademy.name}
                  onChange={(e) => setNewAcademy({ ...newAcademy, name: e.target.value })}
                  placeholder="Academy Name"
                  className="bg-background/50 border-border/30 mt-1"
                />
              </div>
            </div>
            <div className="mb-4">
              <label className="text-xs text-muted-foreground uppercase tracking-wider">Tagline</label>
              <Input
                value={newAcademy.tagline}
                onChange={(e) => setNewAcademy({ ...newAcademy, tagline: e.target.value })}
                placeholder="Short tagline"
                className="bg-background/50 border-border/30 mt-1"
              />
            </div>
            <div className="mb-4">
              <label className="text-xs text-muted-foreground uppercase tracking-wider">Description</label>
              <Textarea
                value={newAcademy.description}
                onChange={(e) => setNewAcademy({ ...newAcademy, description: e.target.value })}
                placeholder="Full description"
                className="bg-background/50 border-border/30 mt-1"
                rows={3}
              />
            </div>
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => setIsCreatingAcademy(false)}>Cancel</Button>
              <Button variant="default" onClick={handleCreateAcademy}>
                <Check className="w-4 h-4 mr-2" />
                Create Academy
              </Button>
            </div>
          </motion.div>
        )}

        {/* Academies List */}
        <div className="space-y-4">
          {academies.map((academy, index) => (
            <motion.div
              key={academy.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
              className="border border-border/30 bg-card/30 backdrop-blur-sm rounded-lg overflow-hidden"
            >
              {/* Academy Header */}
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/5 transition-colors"
                onClick={() => setExpandedAcademy(expandedAcademy === academy.id ? null : academy.id)}
              >
                <div className="flex items-center gap-3">
                  {expandedAcademy === academy.id ? (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  )}
                  <GraduationCap className="w-5 h-5 text-primary" />
                  <div>
                    <h3 className="text-foreground font-medium">{academy.name}</h3>
                    <p className="text-xs text-muted-foreground">{academy.modules?.length || 0} modules • {academy.tagline}</p>
                  </div>
                  {!academy.is_active && (
                    <span className="text-xs px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                      Inactive
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setEditingAcademy(academy)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteAcademy(academy.id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Expanded Academy Content */}
              {expandedAcademy === academy.id && (
                <div className="border-t border-border/20 p-4">
                  <p className="text-sm text-muted-foreground mb-4">{academy.description}</p>
                  
                  {/* Modules */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-foreground">Modules</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsCreatingModule(academy.id)}
                        className="text-primary"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Module
                      </Button>
                    </div>

                    {/* Create Module Form */}
                    {isCreatingModule === academy.id && (
                      <div className="p-4 border border-primary/30 bg-background/50 rounded-lg mb-3">
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="text-xs text-muted-foreground">Title</label>
                            <Input
                              value={newModule.title}
                              onChange={(e) => setNewModule({ ...newModule, title: e.target.value })}
                              placeholder="Module title"
                              className="bg-background/50 border-border/30 mt-1"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground">Transformation</label>
                            <Input
                              value={newModule.transformation}
                              onChange={(e) => setNewModule({ ...newModule, transformation: e.target.value })}
                              placeholder="What students will learn"
                              className="bg-background/50 border-border/30 mt-1"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="text-xs text-muted-foreground">Skills (comma-separated)</label>
                            <Input
                              value={newModule.skills}
                              onChange={(e) => setNewModule({ ...newModule, skills: e.target.value })}
                              placeholder="Skill 1, Skill 2, Skill 3"
                              className="bg-background/50 border-border/30 mt-1"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground">Tech (comma-separated)</label>
                            <Input
                              value={newModule.tech}
                              onChange={(e) => setNewModule({ ...newModule, tech: e.target.value })}
                              placeholder="Tool 1, Tool 2"
                              className="bg-background/50 border-border/30 mt-1"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setIsCreatingModule(null)}>Cancel</Button>
                          <Button variant="default" size="sm" onClick={() => handleCreateModule(academy.id)}>
                            Create Module
                          </Button>
                        </div>
                      </div>
                    )}

                    {academy.modules?.map((module) => (
                      <div
                        key={module.id}
                        className="p-3 border border-border/20 bg-background/30 rounded-lg"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-primary font-mono">#{module.module_number}</span>
                            <BookOpen className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm text-foreground">{module.title}</span>
                            {!module.is_active && (
                              <span className="text-xs px-1 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
                                Inactive
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditingModule(module)}
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteModule(module.id)}
                              className="h-8 w-8 text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        {module.transformation && (
                          <p className="text-xs text-muted-foreground mt-2 ml-16">{module.transformation}</p>
                        )}
                      </div>
                    ))}

                    {(!academy.modules || academy.modules.length === 0) && (
                      <p className="text-sm text-muted-foreground text-center py-4">No modules yet</p>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          ))}

          {academies.length === 0 && (
            <div className="text-center py-12">
              <GraduationCap className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No academies yet</p>
              <p className="text-sm text-muted-foreground mb-6">
                Click "Seed from Static Data" to import existing academies or create a new one.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Academy Modal */}
      {editingAcademy && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
          onClick={() => setEditingAcademy(null)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-full max-w-lg bg-card border border-border/30 rounded-xl p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-light text-foreground">Edit Academy</h3>
              <Button variant="ghost" size="icon" onClick={() => setEditingAcademy(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider">Name</label>
                <Input
                  value={editingAcademy.name}
                  onChange={(e) => setEditingAcademy({ ...editingAcademy, name: e.target.value })}
                  className="bg-background/50 border-border/30 mt-1"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider">Tagline</label>
                <Input
                  value={editingAcademy.tagline || ''}
                  onChange={(e) => setEditingAcademy({ ...editingAcademy, tagline: e.target.value })}
                  className="bg-background/50 border-border/30 mt-1"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider">Description</label>
                <Textarea
                  value={editingAcademy.description}
                  onChange={(e) => setEditingAcademy({ ...editingAcademy, description: e.target.value })}
                  className="bg-background/50 border-border/30 mt-1"
                  rows={3}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingAcademy.is_active}
                  onChange={(e) => setEditingAcademy({ ...editingAcademy, is_active: e.target.checked })}
                  className="rounded"
                />
                <label className="text-sm text-muted-foreground">Active</label>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="ghost" className="flex-1" onClick={() => setEditingAcademy(null)}>Cancel</Button>
              <Button variant="default" className="flex-1" onClick={handleUpdateAcademy}>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Edit Module Modal */}
      {editingModule && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
          onClick={() => setEditingModule(null)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-full max-w-2xl bg-card border border-border/30 rounded-xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-light text-foreground">Edit Module #{editingModule.module_number}</h3>
              <Button variant="ghost" size="icon" onClick={() => setEditingModule(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider">Title</label>
                <Input
                  value={editingModule.title}
                  onChange={(e) => setEditingModule({ ...editingModule, title: e.target.value })}
                  className="bg-background/50 border-border/30 mt-1"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider">Transformation</label>
                <Input
                  value={editingModule.transformation || ''}
                  onChange={(e) => setEditingModule({ ...editingModule, transformation: e.target.value })}
                  className="bg-background/50 border-border/30 mt-1"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider">Description</label>
                <Textarea
                  value={editingModule.description || ''}
                  onChange={(e) => setEditingModule({ ...editingModule, description: e.target.value })}
                  className="bg-background/50 border-border/30 mt-1"
                  rows={2}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider">Full Description</label>
                <Textarea
                  value={editingModule.full_description || ''}
                  onChange={(e) => setEditingModule({ ...editingModule, full_description: e.target.value })}
                  className="bg-background/50 border-border/30 mt-1"
                  rows={4}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider">Skills (one per line)</label>
                <Textarea
                  value={editingModule.skills?.join('\n') || ''}
                  onChange={(e) => setEditingModule({ ...editingModule, skills: e.target.value.split('\n').filter(s => s.trim()) })}
                  className="bg-background/50 border-border/30 mt-1"
                  rows={3}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider">Tech/Tools (one per line)</label>
                <Textarea
                  value={editingModule.tech?.join('\n') || ''}
                  onChange={(e) => setEditingModule({ ...editingModule, tech: e.target.value.split('\n').filter(t => t.trim()) })}
                  className="bg-background/50 border-border/30 mt-1"
                  rows={3}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingModule.is_active}
                  onChange={(e) => setEditingModule({ ...editingModule, is_active: e.target.checked })}
                  className="rounded"
                />
                <label className="text-sm text-muted-foreground">Active</label>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="ghost" className="flex-1" onClick={() => setEditingModule(null)}>Cancel</Button>
              <Button variant="default" className="flex-1" onClick={handleUpdateModule}>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default AdminContent;
